import { useEffect, useState } from "react";
import { Loader2, Plus, Send, Trash2, Wand2 } from "lucide-react";

const gbp = (n) => `£${Number(n || 0).toFixed(2)}`;

export function QuoteBuilder({ lead, api }) {
  const [quotes, setQuotes] = useState([]);
  const [items, setItems] = useState([{ description: "", qty: 1, price: "" }]);
  const [vat, setVat] = useState(false);
  const [discount, setDiscount] = useState("");
  const [notes, setNotes] = useState("");
  const [depositPct, setDepositPct] = useState(50);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = () =>
    api.get("/quotes", { params: { lead_id: lead.id } }).then((r) => setQuotes(r.data.quotes));

  useEffect(() => {
    load().catch(() => {});
    api.get("/settings").then((r) => setDepositPct(r.data.deposit_percent ?? 50)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead.id]);

  const setItem = (i, k, v) =>
    setItems((rows) => rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  const importMeasurements = () => {
    const rows = [];
    (lead.measurement_rooms || []).forEach((room) =>
      (room.windows || []).forEach((w) => {
        if (w.blindType && Number(w.price) > 0) {
          rows.push({
            description: `${room.name}${w.ref ? ` ${w.ref}` : ""} — ${w.blindType} blind, ${w.width}×${w.drop}${w.unit}${w.material ? `, ${w.material}` : ""}, ${w.sizeType.toLowerCase()}, supplied & fitted`,
            qty: Number(w.qty) || 1,
            price: String(w.price),
          });
        }
      }),
    );
    if (rows.length) setItems(rows);
  };

  const subtotal = items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.price) || 0), 0);
  const disc = Math.min(Number(discount) || 0, subtotal);
  const net = subtotal - disc;
  const vatAmount = vat ? net * 0.2 : 0;
  const total = net + vatAmount;
  const deposit = (total * depositPct) / 100;
  const canSave = items.some((i) => i.description.trim()) && !busy;

  const saveDraft = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post("/quotes", {
        lead_id: lead.id,
        items: items
          .filter((i) => i.description.trim())
          .map((i) => ({ description: i.description.trim(), qty: Number(i.qty) || 1, price: Number(i.price) || 0 })),
        vat,
        discount: disc,
        notes: notes || null,
      });
      setItems([{ description: "", qty: 1, price: "" }]);
      setNotes("");
      setDiscount("");
      setVat(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not save quote");
    } finally {
      setBusy(false);
    }
  };

  const sendQuote = async (id) => {
    setBusy(true);
    setError("");
    try {
      await api.post(`/quotes/${id}/send`);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not send quote");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-6" data-testid="quote-builder">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
        Quotations
      </h3>
      {quotes.length > 0 && (
        <ul className="space-y-2 mb-4">
          {quotes.map((q) => (
            <li key={q.id} data-testid={`quote-row-${q.id}`} className="rounded-xl border border-warm-200 p-3.5 flex items-center gap-3">
              <div className="min-w-0">
                <p className="text-xs font-semibold text-slate-800">
                  {q.ref || "Q-" + q.id.slice(0, 8).toUpperCase()} · {gbp(q.total)}
                </p>
                <p className="text-[11px] text-slate-500 truncate">
                  {q.items.map((i) => i.description).join(", ")}
                </p>
              </div>
              <span className={`ml-auto rounded-full px-2.5 py-1 text-[10px] font-bold whitespace-nowrap ${q.status === "Accepted" ? "bg-emerald-600 text-white" : q.status === "Quote Sent" ? "bg-amber-100 text-amber-900" : "bg-slate-100 text-slate-600"}`}>
                {q.status === "Accepted" ? "Accepted" : q.status === "Quote Sent" ? "Sent" : "Draft"}
              </span>
              {q.status === "Quote Created" && lead.email && (
                <button
                  type="button"
                  data-testid={`quote-send-${q.id}`}
                  disabled={busy}
                  onClick={() => sendQuote(q.id)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-navy-900 px-3.5 py-1.5 text-[11px] font-semibold text-white hover:bg-navy-800 disabled:opacity-40"
                >
                  <Send className="h-3 w-3" /> Email to customer
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="rounded-xl border border-warm-200 p-4 space-y-2.5">
        {lead.measurement_rooms?.length > 0 && (
          <button
            type="button"
            data-testid="quote-import-measurements"
            onClick={importMeasurements}
            className="inline-flex items-center gap-1.5 rounded-full bg-warm-100 border border-warm-300 px-3.5 py-2 text-[11px] font-semibold text-navy-900 hover:bg-warm-200"
          >
            <Wand2 className="h-3.5 w-3.5" /> Import priced measurements
          </button>
        )}
        {items.map((item, i) => (
          <div key={i} className="flex gap-2">
            <input
              data-testid={`quote-item-desc-${i}`}
              value={item.description}
              onChange={(e) => setItem(i, "description", e.target.value)}
              placeholder="e.g. 3 × roller blinds, blackout, fitted"
              className="flex-1 rounded-lg border border-warm-300 px-3 py-2 text-xs focus:border-navy-900 focus:outline-none"
            />
            <input
              data-testid={`quote-item-qty-${i}`}
              type="number"
              min="1"
              value={item.qty}
              onChange={(e) => setItem(i, "qty", e.target.value)}
              aria-label="Quantity"
              className="w-16 rounded-lg border border-warm-300 px-2 py-2 text-xs focus:border-navy-900 focus:outline-none"
            />
            <input
              data-testid={`quote-item-price-${i}`}
              type="number"
              min="0"
              step="0.01"
              value={item.price}
              onChange={(e) => setItem(i, "price", e.target.value)}
              placeholder="£ each"
              aria-label="Price each"
              className="w-24 rounded-lg border border-warm-300 px-2 py-2 text-xs focus:border-navy-900 focus:outline-none"
            />
            {items.length > 1 && (
              <button type="button" aria-label="Remove item" onClick={() => setItems((r) => r.filter((_, x) => x !== i))} className="text-slate-400 hover:text-red-600">
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          data-testid="quote-add-item"
          onClick={() => setItems((r) => [...r, { description: "", qty: 1, price: "" }])}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy-900 hover:text-navy-800"
        >
          <Plus className="h-3.5 w-3.5" /> Add item
        </button>
        <div className="flex gap-2">
          <input
            data-testid="quote-discount"
            type="number"
            min="0"
            step="0.01"
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
            placeholder="Discount £"
            className="w-32 rounded-lg border border-warm-300 px-3 py-2 text-xs focus:border-navy-900 focus:outline-none"
          />
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" data-testid="quote-vat" checked={vat} onChange={(e) => setVat(e.target.checked)} className="h-3.5 w-3.5 accent-[#0B1D3A]" />
            Add VAT (20%)
          </label>
        </div>
        <textarea
          data-testid="quote-notes"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes for the customer (optional)"
          className="w-full rounded-lg border border-warm-300 px-3 py-2 text-xs focus:border-navy-900 focus:outline-none resize-none"
        />
        <div className="rounded-lg bg-warm-100 px-3 py-2.5 text-[11px] text-slate-600 space-y-0.5" data-testid="quote-totals">
          <p className="flex justify-between"><span>Subtotal{disc ? ` − ${gbp(disc)} discount` : ""}{vat ? " + VAT" : ""}</span><span className="font-semibold text-slate-900">Total {gbp(total)}</span></p>
          <p className="flex justify-between"><span>Deposit to confirm ({depositPct}%)</span><span>{gbp(deposit)}</span></p>
          <p className="flex justify-between"><span>Balance on completion</span><span>{gbp(total - deposit)}</span></p>
        </div>
        <div className="flex items-center justify-between border-t border-warm-200 pt-3">
          <p className="text-sm font-semibold text-slate-900" data-testid="quote-total">Total {gbp(total)}</p>
          <button
            type="button"
            data-testid="quote-save-draft"
            disabled={!canSave}
            onClick={saveDraft}
            className="inline-flex items-center gap-2 rounded-full bg-navy-900 px-5 py-2.5 text-xs font-semibold text-white hover:bg-navy-800 disabled:opacity-40"
          >
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Save Quote
          </button>
        </div>
        {error && <p role="alert" className="text-xs text-red-700">{error}</p>}
      </div>
    </div>
  );
}
